import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabaseService } from '../lib/supabase-service';
import { authService } from '../lib/auth-service';
import { notificationManager } from '../lib/notification-manager';
import { supabase } from '../lib/supabase';

// Only log in development to improve production performance
const isDev = import.meta.env.MODE === 'development';
const devLog = (...args) => isDev && console.log(...args);
const devWarn = (...args) => isDev && console.warn(...args);
const devError = (...args) => console.error(...args); // Always show errors

// Cache versioning to prevent conflicts
const CACHE_VERSION = 'v2.0';
const getCacheKey = (userId) => `family-data-${userId}-${CACHE_VERSION}`;
const getCacheTimestampKey = (userId) => `family-data-timestamp-${userId}-${CACHE_VERSION}`;

// Enhanced localStorage manager for offline-first approach
const localStorageManager = {
  // Store data with metadata for better management
  setItem(key, value, options = {}) {
    try {
      const { 
        ttl = null, // Time to live in ms
        priority = 'normal', // high, normal, low
        compress = false,
        tags = [] // For grouped invalidation
      } = options;
      
      const data = {
        value,
        timestamp: Date.now(),
        ttl,
        priority,
        tags,
        version: CACHE_VERSION
      };
      
      if (compress && typeof value === 'object') {
        // Simple compression for large objects
        data.value = JSON.stringify(value);
        data.compressed = true;
      }
      
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      // Handle storage quota exceeded
      if (error.name === 'QuotaExceededError') {
        this.cleanupStorage();
        try {
          localStorage.setItem(key, JSON.stringify({ value, timestamp: Date.now() }));
          return true;
        } catch (retryError) {
          devError('LocalStorage quota exceeded even after cleanup:', retryError);
          return false;
        }
      }
      devError('Error setting localStorage item:', error);
      return false;
    }
  },

  getItem(key) {
    try {
      const rawData = localStorage.getItem(key);
      if (!rawData) return null;
      
      const data = JSON.parse(rawData);
      
      // Check TTL expiration
      if (data.ttl && Date.now() - data.timestamp > data.ttl) {
        localStorage.removeItem(key);
        return null;
      }
      
      // Handle compressed data
      if (data.compressed) {
        return JSON.parse(data.value);
      }
      
      return data.value;
    } catch (error) {
      devError('Error getting localStorage item:', error);
      return null;
    }
  },

  // Smart storage cleanup when quota is exceeded
  cleanupStorage() {
    try {
      const allKeys = Object.keys(localStorage);
      const itemsWithMetadata = [];
      
      allKeys.forEach(key => {
        try {
          const rawData = localStorage.getItem(key);
          const data = JSON.parse(rawData);
          if (data.timestamp && data.priority) {
            itemsWithMetadata.push({
              key,
              timestamp: data.timestamp,
              priority: data.priority,
              size: rawData.length
            });
          }
        } catch (e) {
          // Invalid format, remove
          localStorage.removeItem(key);
        }
      });
      
      // Sort by priority (low first) and age (old first)
      itemsWithMetadata.sort((a, b) => {
        const priorityOrder = { low: 0, normal: 1, high: 2 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        return a.timestamp - b.timestamp;
      });
      
      // Remove up to 30% of items or until we free 2MB
      let freedSpace = 0;
      const targetSpace = 2 * 1024 * 1024; // 2MB
      const maxItemsToRemove = Math.floor(itemsWithMetadata.length * 0.3);
      
      for (let i = 0; i < maxItemsToRemove && freedSpace < targetSpace; i++) {
        const item = itemsWithMetadata[i];
        localStorage.removeItem(item.key);
        freedSpace += item.size;
        devLog(`🧹 Cleaned up ${item.key} (${(item.size / 1024).toFixed(1)}KB)`);
      }
      
      devLog(`🧹 Storage cleanup complete: freed ${(freedSpace / 1024).toFixed(1)}KB`);
    } catch (error) {
      devError('Error during storage cleanup:', error);
    }
  },

  // Clear items by tags
  clearByTags(tags) {
    Object.keys(localStorage).forEach(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (data.tags && data.tags.some(tag => tags.includes(tag))) {
          localStorage.removeItem(key);
        }
      } catch (e) {
        // Invalid format, skip
      }
    });
  },

  // Get storage usage info
  getStorageInfo() {
    let totalSize = 0;
    let itemCount = 0;
    
    Object.keys(localStorage).forEach(key => {
      totalSize += localStorage.getItem(key).length;
      itemCount++;
    });
    
    const quotaEstimate = 10 * 1024 * 1024; // 10MB typical
    return {
      usedBytes: totalSize,
      usedMB: (totalSize / (1024 * 1024)).toFixed(2),
      itemCount,
      quotaUsedPercent: ((totalSize / quotaEstimate) * 100).toFixed(1)
    };
  }
};

// Optimistic update manager with rollback capability
const optimisticUpdateManager = {
  pendingUpdates: new Map(),
  
  // Apply optimistic update with rollback capability
  applyOptimistic(key, newData, rollbackData) {
    // Store original data for rollback
    this.pendingUpdates.set(key, {
      original: rollbackData,
      timestamp: Date.now()
    });
    
    // Apply optimistic update to localStorage immediately
    localStorageManager.setItem(`optimistic-${key}`, newData, {
      ttl: 30000, // 30 seconds max for optimistic updates
      priority: 'high',
      tags: ['optimistic']
    });
    
    devLog(`⚡ Optimistic update applied for ${key}`);
    return newData;
  },
  
  // Confirm successful server update
  confirmUpdate(key) {
    this.pendingUpdates.delete(key);
    localStorage.removeItem(`optimistic-${key}`);
    devLog(`✅ Optimistic update confirmed for ${key}`);
  },
  
  // Rollback failed update
  rollbackUpdate(key) {
    const pending = this.pendingUpdates.get(key);
    if (pending) {
      // Restore original data
      localStorageManager.setItem(key, pending.original, {
        priority: 'high',
        tags: ['restored']
      });
      
      this.pendingUpdates.delete(key);
      localStorage.removeItem(`optimistic-${key}`);
      devWarn(`🔄 Rolled back optimistic update for ${key}`);
      
      return pending.original;
    }
    return null;
  },
  
  // Clean up old pending updates
  cleanupStaleUpdates() {
    const now = Date.now();
    for (const [key, data] of this.pendingUpdates.entries()) {
      if (now - data.timestamp > 30000) { // 30 seconds
        this.rollbackUpdate(key);
      }
    }
  }
};

// User preferences and settings manager
const userPreferencesManager = {
  // Save user preferences with automatic merging
  savePreferences(userId, preferences) {
    const existing = this.getPreferences(userId);
    const merged = { ...existing, ...preferences };
    
    localStorageManager.setItem(`user-prefs-${userId}`, merged, {
      priority: 'high', // User preferences are important
      tags: ['preferences', 'user-data']
    });
    
    devLog('💾 User preferences saved:', preferences);
    return merged;
  },
  
  // Get user preferences with defaults
  getPreferences(userId, defaults = {}) {
    const prefs = localStorageManager.getItem(`user-prefs-${userId}`) || {};
    return { ...defaults, ...prefs };
  },
  
  // Common preference categories
  getUIPreferences(userId) {
    return this.getPreferences(userId, {
      theme: 'light',
      sidebarCollapsed: false,
      defaultView: 'dashboard',
      showCompletedTasks: false,
      taskSortOrder: 'priority',
      shoppingListView: 'category', // category, store, alphabetical
      familyActivityView: 'grid', // grid, list
      allowanceDisplayMode: 'simple', // simple, detailed
      notificationSettings: {
        shopping: true,
        tasks: true,
        allowance: true,
        messages: true
      }
    });
  },
  
  saveUIPreference(userId, key, value) {
    const current = this.getUIPreferences(userId);
    current[key] = value;
    return this.savePreferences(userId, { ui: current });
  },
  
  // App-specific settings
  getAppSettings(userId) {
    return this.getPreferences(userId, {
      autoSaveInterval: 30000, // 30 seconds
      offlineMode: false,
      debugMode: false,
      cacheStrategy: 'aggressive', // conservative, balanced, aggressive
      syncFrequency: 'realtime', // realtime, periodic, manual
      dataRetentionDays: 90
    });
  }
};

// Draft management for forms and content
const draftManager = {
  // Save draft with automatic cleanup
  saveDraft(key, content, options = {}) {
    const { ttl = 24 * 60 * 60 * 1000 } = options; // 24 hours default
    
    localStorageManager.setItem(`draft-${key}`, {
      content,
      savedAt: Date.now(),
      autoSaved: options.autoSaved || false
    }, {
      ttl,
      priority: 'normal',
      tags: ['draft', 'user-content']
    });
    
    devLog(`📝 Draft saved: ${key}`);
  },
  
  // Get draft content
  getDraft(key) {
    const draft = localStorageManager.getItem(`draft-${key}`);
    return draft ? draft.content : null;
  },
  
  // Clear specific draft
  clearDraft(key) {
    localStorage.removeItem(`draft-${key}`);
    devLog(`🗑️ Draft cleared: ${key}`);
  },
  
  // Get all drafts for user
  getAllDrafts(userId) {
    const drafts = [];
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(`draft-${userId}-`)) {
        const draft = localStorageManager.getItem(key);
        if (draft) {
          drafts.push({
            key: key.replace(`draft-${userId}-`, ''),
            content: draft.content,
            savedAt: draft.savedAt,
            autoSaved: draft.autoSaved
          });
        }
      }
    });
    
    return drafts.sort((a, b) => b.savedAt - a.savedAt);
  },
  
  // Auto-save with debouncing
  autoSave: (() => {
    const timers = new Map();
    
    return (key, content, delay = 3000) => {
      // Clear existing timer
      if (timers.has(key)) {
        clearTimeout(timers.get(key));
      }
      
      // Set new timer
      const timer = setTimeout(() => {
        this.saveDraft(key, content, { autoSaved: true });
        timers.delete(key);
      }, delay);
      
      timers.set(key, timer);
    };
  })()
};

// Separate cache for family structure (avatars, names, etc.) - longer duration
const getFamilyStructureCacheKey = (userId) => `family-structure-${userId}-${CACHE_VERSION}`;
const getFamilyStructureTimestampKey = (userId) => `family-structure-timestamp-${userId}-${CACHE_VERSION}`;

// Image cache for family activities - cache URLs to avoid expensive restoration (single-family architecture)
const getImageCacheKey = () => `family-images-single-family-${CACHE_VERSION}`;
const getImageCacheTimestampKey = () => `family-images-timestamp-single-family-${CACHE_VERSION}`;

// Historical activities cache - longer duration for completed activities (single-family architecture)
const getHistoricalActivitiesCacheKey = () => `historical-activities-single-family-${CACHE_VERSION}`;
const getHistoricalActivitiesTimestampKey = () => `historical-activities-timestamp-single-family-${CACHE_VERSION}`;

// Performance monitoring helper
const measurePerformance = async (name, fn) => {
  const start = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - start;
    if (duration > 1000) {
      devWarn(`🐌 Slow operation: ${name} took ${Math.round(duration)}ms`);
    } else if (isDev) {
      devLog(`⚡ ${name} completed in ${Math.round(duration)}ms`);
    }
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    devError(`❌ Failed operation: ${name} after ${Math.round(duration)}ms`, error);
    throw error;
  }
};

// Create the context
const FamilyContext = createContext();

// Create the hook
const useFamilyContext = () => {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamilyContext must be used within FamilyProvider');
  }
  return context;
};

// Create the provider component
const FamilyProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [family, setFamily] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [familyUsers, setFamilyUsers] = useState([]);
  const [activities, setActivities] = useState({});
  const [plans, setPlans] = useState({});
  const [patterns, setPatterns] = useState({});
  const [familyActivities, setFamilyActivities] = useState([]);
  const [familyCalendar, setFamilyCalendar] = useState([]);
  const [recsIdeas, setRecsIdeas] = useState([]);
  const [familyTimeInvites, setFamilyTimeInvites] = useState([]);
  const [kidAllowances, setKidAllowances] = useState({});
  const [allowanceTransactions, setAllowanceTransactions] = useState({});
  const [allowanceJobs, setAllowanceJobs] = useState([]);
  const [personalModules, setPersonalModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // Separate state for data refreshes
  const [error, setError] = useState(null);
  const isLoadingDataRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const pendingRequestsRef = useRef(new Map());

  // Get cached family structure (avatars, names, basic info) with 2-hour cache
  const getCachedFamilyStructure = (userId) => {
    try {
      const cachedData = localStorage.getItem(getFamilyStructureCacheKey(userId));
      const cacheTimestamp = localStorage.getItem(getFamilyStructureTimestampKey(userId));
      
      if (cachedData && cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        if (cacheAge < 2 * 60 * 60 * 1000) { // 2 hours - family structure rarely changes
          return JSON.parse(cachedData);
        }
      }
    } catch (error) {
      devError('Error reading family structure cache:', error);
    }
    return null;
  };

  // Cache family structure data
  const cacheFamilyStructure = (userId, familyData) => {
    try {
      // Only cache the structural data (not dynamic content)
      const structureData = {
        id: familyData.id,
        name: familyData.name,
        emoji: familyData.emoji,
        parentName: familyData.parentName,
        parentAvatar: familyData.parentAvatar,
        parents: familyData.parents,
        kids: familyData.kids
      };
      
      localStorage.setItem(getFamilyStructureCacheKey(userId), JSON.stringify(structureData));
      localStorage.setItem(getFamilyStructureTimestampKey(userId), Date.now().toString());
      devLog('💾 Family structure cached for 2 hours');
    } catch (error) {
      devError('Error caching family structure:', error);
    }
  };

  // Get cached family activity images
  const getCachedActivityImages = () => {
    try {
      const cached = localStorage.getItem(getImageCacheKey());
      const timestamp = localStorage.getItem(getImageCacheTimestampKey());
      
      if (cached && timestamp) {
        const cacheAge = Date.now() - parseInt(timestamp);
        if (cacheAge < 6 * 60 * 60 * 1000) { // 6 hours - images don't change often
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      devError('Error reading image cache:', error);
    }
    return null;
  };

  // Cache family activity images
  const cacheActivityImages = (imageMap) => {
    try {
      localStorage.setItem(getImageCacheKey(), JSON.stringify(imageMap));
      localStorage.setItem(getImageCacheTimestampKey(), Date.now().toString());
      devLog('🖼️ Activity images cached for 6 hours');
    } catch (error) {
      devError('Error caching activity images:', error);
    }
  };

  // Get cached historical activities (completed activities older than 7 days)
  const getCachedHistoricalActivities = () => {
    try {
      const cached = localStorage.getItem(getHistoricalActivitiesCacheKey());
      const timestamp = localStorage.getItem(getHistoricalActivitiesTimestampKey());
      
      if (cached && timestamp) {
        const cacheAge = Date.now() - parseInt(timestamp);
        if (cacheAge < 24 * 60 * 60 * 1000) { // 24 hours - historical data rarely changes
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      devError('Error reading historical activities cache:', error);
    }
    return null;
  };

  // Cache historical activities
  const cacheHistoricalActivities = (activities) => {
    try {
      // Only cache completed activities older than 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const historicalActivities = activities.filter(activity => 
        activity.completed && new Date(activity.date) < sevenDaysAgo
      );
      
      localStorage.setItem(getHistoricalActivitiesCacheKey(), JSON.stringify(historicalActivities));
      localStorage.setItem(getHistoricalActivitiesTimestampKey(), Date.now().toString());
      devLog(`📚 Cached ${historicalActivities.length} historical activities for 24 hours`);
    } catch (error) {
      devError('Error caching historical activities:', error);
    }
  };

  // Get family activities with historical caching
  const getFamilyActivitiesWithCache = async () => {
    // Get fresh activities for recent/active items
    const allActivities = await supabaseService.getFamilyActivities();
    
    // Get cached historical activities
    const cachedHistorical = getCachedHistoricalActivities();
    
    if (cachedHistorical && cachedHistorical.length > 0) {
      // Filter out historical activities from fresh data to avoid duplicates
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const recentActivities = allActivities.filter(activity => 
        !activity.completed || new Date(activity.date) >= sevenDaysAgo
      );
      
      // Combine recent activities with cached historical ones
      const combinedActivities = [...recentActivities, ...cachedHistorical];
      devLog(`🚀 Using ${cachedHistorical.length} cached historical activities`);
      
      // Cache historical activities for next time
      cacheHistoricalActivities(allActivities);
      
      return combinedActivities;
    } else {
      // No cache, use all fresh data and cache historical portion
      cacheHistoricalActivities(allActivities);
      return allActivities;
    }
  };

  // Request deduplication helper with timeout protection
  const deduplicatedRequest = async (key, requestFn) => {
    if (pendingRequestsRef.current.has(key)) {
      devLog(`⏱️  Returning existing request for: ${key}`);
      return pendingRequestsRef.current.get(key);
    }
    
    const promise = requestFn();
    pendingRequestsRef.current.set(key, promise);
    
    // Add timeout protection to prevent hanging requests
    // Increased timeout for family data loading to handle large families and slow connections
    const timeoutDuration = key.includes('loadFamilyData') ? 60000 : 30000; // 60s for family data, 30s for others
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        pendingRequestsRef.current.delete(key);
        reject(new Error(`Request timeout for: ${key} (${timeoutDuration/1000}s)`));
      }, timeoutDuration);
    });
    
    try {
      const result = await Promise.race([promise, timeoutPromise]);
      pendingRequestsRef.current.delete(key);
      return result;
    } catch (error) {
      pendingRequestsRef.current.delete(key);
      devError(`Request failed for ${key}:`, error);
      throw error;
    }
  };

  // Clear stale localStorage cache and old versions
  const clearStaleCache = (userId) => {
    try {
      const currentCacheKey = getCacheKey(userId);
      const currentTimestampKey = getCacheTimestampKey(userId);
      
      Object.keys(localStorage).forEach(key => {
        // Remove old cache versions
        if (key.startsWith('family-data-') && key !== currentCacheKey && key !== currentTimestampKey) {
          localStorage.removeItem(key);
          devLog(`🧹 Removed old cache: ${key}`);
        }
      });
      devLog('🧹 Cleared stale cache entries');
    } catch (error) {
      devError('Failed to clear stale cache:', error);
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      devLog('🚀 Starting auth initialization...');
      setLoading(true); // Ensure loading is set
      
      try {
        // Getting auth session...
        
        // Check if authService exists
        if (!authService || typeof authService.getSession !== 'function') {
          throw new Error('Auth service not available');
        }
        
        // Try to get session without timeout first - auth should be fast
        let session;
        try {
          // Trying direct supabase auth...
          const { data: { session: directSession }, error } = await supabase.auth.getSession();
          if (error) throw error;
          session = directSession;
          devLog('Direct session result:', !!session?.user);
        } catch (directError) {
          console.error('Direct auth failed:', directError);
          
          // Fallback to authService with timeout
          try {
            const sessionPromise = authService.getSession();
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Auth service timeout')), 10000)
            );
            
            session = await Promise.race([sessionPromise, timeoutPromise]);
            devLog('AuthService session result:', !!session?.user);
          } catch (fallbackError) {
            console.error('AuthService also failed:', fallbackError);
            devLog('Continuing without auth session');
            session = null;
          }
        }
        devLog('📋 Session check complete:', !!session?.user);
        
        if (session?.user) {
          setUser(session.user);
          devLog('👤 User set, loading family data...');
          
          // Set a temporary currentUser immediately to prevent "User" display
          // This will be replaced with full data once family loads
          if (!currentUser) {
            const tempUser = {
              id: session.user.id,
              name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Loading...',
              email: session.user.email,
              authUserId: session.user.id,
              isTemporary: true // Flag to identify this is temporary
            };
            setCurrentUser(tempUser);
            devLog('🔄 Set temporary currentUser to prevent "User" display:', tempUser.name);
          }
          
          devLog('Loading family data for user:', session.user.id);
          try {
            await loadFamilyData(session.user.id);
            devLog('✅ Family data loaded successfully');
          } catch (loadError) {
            console.error('Error loading family data:', loadError);
            devError('❗ Error loading family data:', loadError);
            setError(loadError.message);
            
            // Don't block the UI completely - try to load from cache
            try {
              const cachedData = localStorage.getItem(getCacheKey(session.user.id));
              if (cachedData) {
                const parsedData = JSON.parse(cachedData);
                setFamily(parsedData.family);
                setFamilyActivities(parsedData.familyActivities || []);
                setFamilyCalendar(parsedData.familyCalendar || []);
                setActivities(parsedData.activities || {});
                setPlans(parsedData.plans || {});
                setKidAllowances(parsedData.kidAllowances || {});
                setAllowanceTransactions(parsedData.allowanceTransactions || {});
                setAllowanceJobs(parsedData.allowanceJobs || []);
                setPersonalModules(parsedData.personalModules || {});
                
                // Also restore currentUser (replace temporary if needed)
                if (parsedData.currentUser) {
                  setCurrentUser(parsedData.currentUser);
                } else if (parsedData.family && session.user.id) {
                  const parent = parsedData.family.parents?.find(p => p.authUserId === session.user.id);
                  if (parent) {
                    setCurrentUser({
                      ...parent,
                      isParent: true,
                      isOwner: parent.isOwner
                    });
                  } else if (!currentUser || currentUser.isTemporary) {
                    // Still set temporary user if no parent found in cache
                    const tempUser = {
                      id: session.user.id,
                      name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Loading...',
                      email: session.user.email,
                      authUserId: session.user.id,
                      isTemporary: true
                    };
                    setCurrentUser(tempUser);
                  }
                }
                devLog('📦 Fallback to cached data due to loading error');
              }
            } catch (cacheError) {
              devError('❗ Failed to load from cache:', cacheError);
            }
          }
        } else {
          devLog('❌ No session found');
        }
      } catch (err) {
        devError('❗ Auth initialization error:', err);
        setError(err.message);
      } finally {
        console.log('🚨 FamilyContext: Auth initialization complete');
        devLog('🏁 Auth initialization complete');
        setAuthInitialized(true);
        setLoading(false);
        console.log('🚨 FamilyContext: Loading set to false, authInitialized set to true');
        devLog('✅ Loading state set to false');
      }
    };

    // Initialize immediately
    initAuth();

    // Handle tab visibility changes to preserve data
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user && family) {
        devLog('👁️ Tab became visible, checking data integrity...');
        
        // Only reload if we've truly lost critical data AND haven't loaded recently
        const now = Date.now();
        const timeSinceLastLoad = now - lastLoadTimeRef.current;
        
        if (familyActivities.length === 0 && family && timeSinceLastLoad > 30000) { // 30 seconds
          devLog('🔄 Data appears to be lost and enough time passed, reloading...');
          // Use isRefreshing state instead of loading for tab switches
          setIsRefreshing(true);
          loadFamilyData(user.id, true).finally(() => {
            setIsRefreshing(false);
          });
        } else if (timeSinceLastLoad <= 30000) {
          devLog('⏰ Data loaded recently, skipping reload to prevent unnecessary loading');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    const cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    
    // Listen for auth changes
    const { data: { subscription } } = authService.onAuthStateChange(async (event, session) => {
      devLog('Auth state change:', event, 'User:', session?.user?.id, 'Current user:', user?.id);
      console.log('🔐 Auth event:', event, 'Session expires at:', session?.expires_at ? new Date(session.expires_at * 1000) : 'No session');
      console.log('🚨 Auth state change debug:', {
        event,
        hasSession: !!session?.user,
        sessionUserId: session?.user?.id,
        currentUserId: user?.id,
        currentFamily: !!family
      });
      
      // Ignore token refreshes, initial sessions, user updates, and same-user events to prevent unnecessary reloads
      if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') {
        devLog('Ignoring non-critical auth event:', event);
        return;
      }
      
      // Only ignore for non-SIGNED_IN events if it's the same user
      if (session?.user?.id === user?.id && event !== 'SIGNED_IN') {
        devLog('Same user, ignoring auth event to preserve data');
        return;
      }
      
      // Additional safeguard: If we have a valid session but user is null, this might be a session restoration
      if (session?.user && !user && event !== 'SIGNED_OUT') {
        devLog('Session exists but user is null, this might be a session restoration');
        setUser(session.user);
        return;
      }
      
      // Only handle actual sign in/out events for different users
      console.log('🚨 Auth event check:', {
        event,
        hasSession: !!session?.user,
        currentUser: !!user,
        condition: event === 'SIGNED_IN' && session?.user && !user
      });
      
      // Simplified logic: Always load family data on SIGNED_IN if we have a session
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('🚨 SIGNED_IN event - Loading family data for user:', session.user.id);
        setUser(session.user);
        
        try {
          console.log('🚨 Calling loadFamilyData...');
          await loadFamilyData(session.user.id, true); // Force reload to ensure fresh data
          console.log('🚨 Family data loaded successfully from SIGNED_IN event');
        } catch (loadError) {
          console.error('🚨 Error loading family data from SIGNED_IN event:', loadError);
          setError(loadError.message);
        }
      }
      
      // Additional check: If user is set but no family data, load it
      if (event === 'SIGNED_IN' && session?.user && !family) {
        console.log('🚨 User signed in but no family data, loading...');
        try {
          await loadFamilyData(session.user.id, true);
          console.log('🚨 Family data loaded for user without family');
        } catch (loadError) {
          console.error('🚨 Error loading family data for user without family:', loadError);
        }
      }
      
      if (event === 'SIGNED_OUT') {
        devLog('User signed out, clearing data');
        setUser(null);
        setFamily(null);
        setCurrentUser(null);
        setActivities({});
        setPlans({});
        setPatterns({});
        setFamilyActivities([]);
        setFamilyCalendar([]);
        setRecsIdeas([]);
        setFamilyTimeInvites([]);
        setKidAllowances({});
        setAllowanceTransactions({});
        setAllowanceJobs([]);
        setPersonalModules({});
      }
    });

    return () => {
      subscription.unsubscribe();
      cleanup();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFamilyData = async (userId, forceReload = false) => {
    if (!userId) {
      devLog('No userId provided to loadFamilyData');
      return;
    }

    // Use request deduplication to prevent multiple concurrent loads
    return deduplicatedRequest(`loadFamilyData-${userId}`, async () => {
      // Clear stale cache entries first
      clearStaleCache(userId);

      const now = Date.now();
      devLog(`🔄 Loading family data for userId: ${userId} (force: ${forceReload})`);

      // Additional protection against rapid reloads
      if (!forceReload && family && (now - lastLoadTimeRef.current) < 3000) {
        devLog('Family data loaded recently, using existing data...');
        return;
      }

      // Check for cached family data (valid for 2 minutes to reduce reloads but allow fresh data)
      if (!forceReload) {
        const cachedData = localStorage.getItem(getCacheKey(userId));
        const cacheTimestamp = localStorage.getItem(getCacheTimestampKey(userId));
      
      if (cachedData && cacheTimestamp) {
        const cacheAge = now - parseInt(cacheTimestamp);
        if (cacheAge < 10 * 60 * 1000) { // 10 minutes - family data doesn't change frequently
          try {
            const parsedData = JSON.parse(cachedData);

            // Check cache version - if ideas don't have authorName, invalidate cache
            const cacheVersion = parsedData.cacheVersion || 0;
            const CURRENT_CACHE_VERSION = 2; // Incremented to force refresh for authorName/authorAvatar

            if (cacheVersion >= CURRENT_CACHE_VERSION) {
              // Cache is valid - restore from cache to prevent data loss during tab switches
              setFamily(parsedData.family);
              setActivities(parsedData.activities || {});
              setPlans(parsedData.plans || {});
              setPatterns(parsedData.patterns || {});
              setFamilyActivities(parsedData.familyActivities || []);
              setFamilyCalendar(parsedData.familyCalendar || []);
              setRecsIdeas(parsedData.recsIdeas || []);
              setFamilyTimeInvites(parsedData.familyTimeInvites || []);
              setKidAllowances(parsedData.kidAllowances || {});
              setAllowanceTransactions(parsedData.allowanceTransactions || {});
              setAllowanceJobs(parsedData.allowanceJobs || []);
              setPersonalModules(parsedData.personalModules || {});
              console.log('📦 Loaded family data from cache (age:', Math.round(cacheAge / 1000), 'seconds)');

              // Set family users for user switching from cached data
              if (parsedData.family && familyUsers.length === 0) {
              const allFamilyUsers = [];
              
              // Add all parents
              if (parsedData.family.parents) {
                parsedData.family.parents.forEach(parent => {
                  allFamilyUsers.push({
                    ...parent,
                    isParent: true,
                    displayName: parent.name || 'Parent',
                    type: 'parent'
                  });
                });
              }
              
              // Add all kids
              if (parsedData.family.kids) {
                parsedData.family.kids.forEach(kid => {
                  allFamilyUsers.push({
                    ...kid,
                    isParent: false,
                    displayName: kid.name || 'Student',
                    type: 'student'
                  });
                });
              }
              
              setFamilyUsers(allFamilyUsers);
            }
            
              // Auto-set current user if available in cache (replace temporary if needed)
              if ((!currentUser || currentUser.isTemporary) && parsedData.currentUser) {
                setCurrentUser(parsedData.currentUser);
                devLog('✅ Restored currentUser from cache:', parsedData.currentUser);
              } else if ((!currentUser || currentUser.isTemporary) && parsedData.family && userId) {
                // If no cached currentUser, try to determine it from family data
                const parent = parsedData.family.parents?.find(p => p.authUserId === userId);
                if (parent) {
                  setCurrentUser({
                    ...parent,
                    isParent: true,
                    isOwner: parent.isOwner
                  });
                  devLog('✅ Set currentUser as parent from cached family data');
                }
              }

              // Update last load time when loading from cache
              lastLoadTimeRef.current = now;
              return;
            } else {
              // Cache version is outdated, clear it
              console.log('📦 Cache version outdated (found:', cacheVersion, 'need:', CURRENT_CACHE_VERSION, '), clearing cache...');
              localStorage.removeItem(getCacheKey(userId));
              localStorage.removeItem(getCacheTimestampKey(userId));
              // Will fetch fresh data below
            }
          } catch (err) {
            console.warn('Failed to parse cached data:', err);
            // Clear invalid cache
            localStorage.removeItem(getCacheKey(userId));
            localStorage.removeItem(getCacheTimestampKey(userId));
          }
        }
      }
    }

    // Skip if we loaded recently (within 5 seconds) and have family data
    if (family && (now - lastLoadTimeRef.current) < 5000 && !forceReload) {
      console.log('Family data loaded recently, skipping reload...');
      return;
    }

    try {
      isLoadingDataRef.current = true;
      lastLoadTimeRef.current = now;
      console.log('🔄 Loading family data for userId:', userId);
      
      // Don't set loading state during refreshes - it's only for initial auth
      // The loading state should remain false after initial auth completes
      
      // First, check for cached family structure
      let familyData = getCachedFamilyStructure(userId);
      
      if (familyData) {
        devLog('🚀 Using cached family structure (2hr cache)');
        setFamily(familyData);
        // Set family users for user switching - ensure proper structure
        const allFamilyUsers = [];
        
        // Add all parents with proper structure
        if (familyData.parents) {
          familyData.parents.forEach(parent => {
            allFamilyUsers.push({
              ...parent,
              isParent: true,
              displayName: parent.name || 'Parent',
              type: 'parent'
            });
          });
        }
        
        // Add all kids with proper structure  
        if (familyData.kids) {
          familyData.kids.forEach(kid => {
            allFamilyUsers.push({
              ...kid,
              isParent: false,
              displayName: kid.name || 'Student',
              type: 'student'
            });
          });
        }
        
        setFamilyUsers(allFamilyUsers);
      } else {
        // Fetch fresh family data
        devLog('👨‍👩‍👧‍👦 Fetching family by user ID...');
        console.log('🔍 DEBUG: About to call getFamilyByUserId with userId:', userId);
        
        try {
          familyData = await measurePerformance('getFamilyByUserId', () => 
            supabaseService.getFamilyByUserId(userId)
          );
          console.log('🔍 DEBUG: getFamilyByUserId returned:', familyData);
          devLog('Family data result:', familyData);
        } catch (error) {
          console.error('🔍 DEBUG: getFamilyByUserId failed with error:', error);
          throw error;
        }
        
        if (familyData) {
          // Cache the family structure for future use
          cacheFamilyStructure(userId, familyData);
        }
      }
      
      if (!familyData) {
        // User is not part of any family yet
        console.log('User is not part of any family yet');
        setFamily(null);
        setCurrentUser(null);
        // Don't touch loading state here - it's only for initial auth
        return;
      }

      // Load family data with shorter timeouts
      console.log('Loading all family-related data...');
      
      // Set family immediately so UI can render
      setFamily(familyData);
      
      // Load additional data in background with better error handling and performance monitoring
      const loadWithTimeout = (promise, name, defaultValue = [], timeoutMs = 25000) => {
        // Create timeout promise
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error(`Timeout loading ${name} after ${timeoutMs}ms`));
          }, timeoutMs);
        });
        
        // Race the original promise against the timeout
        const timeoutRacePromise = Promise.race([promise, timeoutPromise]);
        
        return measurePerformance(name, () => timeoutRacePromise).catch(err => {
          devWarn(`Error loading ${name}:`, err);
          // Return appropriate default value based on data type
          return defaultValue;
        });
      };
      
      devLog('📊 Loading family data...');
      const [
        activitiesData,
        plansData,
        patternsData,
        familyActivitiesData,
        calendarData,
        ideasData,
        invitesData,
        jobsData
      ] = await measurePerformance('loadAllFamilyData', () => Promise.all([
        loadWithTimeout(supabaseService.getActivities(familyData.id), 'activities'),
        loadWithTimeout(supabaseService.getPlans(familyData.id), 'plans'),
        loadWithTimeout(supabaseService.getPatterns(familyData.id), 'patterns'),
        loadWithTimeout(getFamilyActivitiesWithCache(familyData.id), 'familyActivities'),
        loadWithTimeout(supabaseService.getCalendarEvents(familyData.id), 'calendarEvents'),
        loadWithTimeout(supabaseService.getIdeas(500), 'ideas'), // Load 500 most recent ideas
        loadWithTimeout(supabaseService.getFamilyTimeInvites(familyData.id), 'familyTimeInvites'),
        loadWithTimeout(supabaseService.getAllJobs(familyData.id).catch(() => []), 'allowanceJobs')
      ]));

      console.log('📊 All family data loaded successfully');
      console.log('📝 Raw data counts:', {
        activities: activitiesData?.length || 0,
        plans: plansData?.length || 0,
        patterns: patternsData?.length || 0,
        familyActivities: familyActivitiesData?.length || 0,
        calendarEvents: calendarData?.length || 0,
        ideas: ideasData?.length || 0,
        invites: invitesData?.length || 0,
        jobs: jobsData?.length || 0
      });
      
      console.log('🔍 Plans data details:', {
        plansData,
        firstPlan: plansData?.[0],
        planWithBig3: plansData?.find(p => p.big_3)
      });

      // Process activities by user (kids AND parents)
      const activitiesByUser = {};
      activitiesData.forEach(activity => {
        // Activities can belong to kids (kidId) or parents (parentId/userId)
        const userId = activity.kidId || activity.parentId || activity.userId;
        if (userId) {
          if (!activitiesByUser[userId]) {
            activitiesByUser[userId] = [];
          }
          activitiesByUser[userId].push(activity);
        }
      });

      // Process plans by user (kids AND parents)
      const plansByUser = {};
      plansData.forEach(plan => {
        // Plans can belong to kids (kid_id) or parents (user_id)  
        const userId = plan.kid_id || plan.user_id;
        if (userId) {
          if (!plansByUser[userId]) {
            plansByUser[userId] = {};
          }
          if (!plansByUser[userId][plan.date]) {
            // Transform plan data to match component expectations
            const transformedPlan = {
              ...plan,
              // If big_3 exists, also add it as big3 for component compatibility
              big3: plan.big_3 || null
            };
            
            console.log('📝 Loading plan for user:', userId, 'date:', plan.date, 'big_3:', plan.big_3);
            plansByUser[userId][plan.date] = transformedPlan;
          }
        }
      });

      // Process patterns by kid
      const patternsByKey = {};
      patternsData.forEach(pattern => {
        const key = `${pattern.kidId}-${pattern.dayOfWeek}`;
        if (!patternsByKey[key]) {
          patternsByKey[key] = [];
        }
        patternsByKey[key].push(pattern);
      });

      // Check cached allowance balances first
      const cachedData = getCachedFamilyStructure(userId);
      const allowancesByKid = {};
      const transactionsByKid = {};
      
      if (cachedData?.allowances && Object.keys(cachedData.allowances).length === familyData.kids.length) {
        devLog('🚀 Using cached allowance balances');
        Object.assign(allowancesByKid, cachedData.allowances);
      } else {
        // Process allowances by kid (fetch real balances)
        devLog('💰 Fetching fresh allowance data for kids...');
        
        // Fetch all kid allowances in parallel
        const kidAllowanceResults = await Promise.all(
          familyData.kids.map(kid => supabaseService.getKidAllowance(kid.id))
        );
        
        familyData.kids.forEach((kid, idx) => {
          const allowance = kidAllowanceResults[idx];
          allowancesByKid[kid.id] = {
            weeklyAmount: kid.weeklyAllowance || 0,
            savingsPercentage: kid.savingsPercentage || 0,
            lastPaid: kid.lastAllowancePaid,
            balance: allowance?.current_balance || 0,
            current_balance: allowance?.current_balance || 0, // for AllowanceTracker compatibility
            enabled: allowance?.enabled ?? true,
            weekly_amount: allowance?.weekly_amount ?? (kid.weeklyAllowance || 0),
            last_weekly_addition: allowance?.last_weekly_addition || null,
            job_scheduler_enabled: allowance?.job_scheduler_enabled || false,
            hourly_rate: allowance?.hourly_rate || 0
          };
          transactionsByKid[kid.id] = [];
        });
        
        // Cache allowances for future use (update family structure cache)
        if (cachedData) {
          const updatedStructure = { ...cachedData, allowances: allowancesByKid };
          cacheFamilyStructure(userId, updatedStructure);
          devLog('💾 Cached fresh allowance balances');
        }
      }

      // Load allowance transactions
      const transactionsData = await loadWithTimeout(
        supabaseService.getFamilyAllowanceTransactions(familyData.id),
        'allowance transactions'
      );

      // Process transactions by kid
      transactionsData.forEach(transaction => {
        if (!transactionsByKid[transaction.kidId]) {
          transactionsByKid[transaction.kidId] = [];
        }
        transactionsByKid[transaction.kidId].push(transaction);
      });

      // Debug family activities data before setting state
      console.log('🔍 Family Activities Debug:', {
        familyActivitiesData,
        familyActivitiesDataLength: familyActivitiesData?.length,
        firstActivity: familyActivitiesData?.[0]
      });
      
      // Log current user status
      console.log('👤 Current User Status:', {
        hasCurrentUser: !!currentUser,
        currentUser: currentUser,
        userId: userId,
        familyParents: familyData.parents
      });

      // Update state with all loaded data
      setActivities(activitiesByUser);
      setPlans(plansByUser);
      setPatterns(patternsByKey);
      setFamilyActivities(familyActivitiesData || []);
      setFamilyCalendar(calendarData);
      setRecsIdeas(ideasData);
      setFamilyTimeInvites(invitesData);
      setKidAllowances(allowancesByKid);
      setAllowanceTransactions(transactionsByKid);
      setAllowanceJobs(jobsData);
      setPersonalModules(personalModules);

      // Try to restore persisted user selection first
      let currentUserToCache = currentUser;
      if (!currentUser) {
        try {
          const persistedSelection = localStorage.getItem('selected-user-single-family');
          if (persistedSelection) {
            const { userId: persistedUserId, isParent, timestamp, originalParent, originalAuthUserId } = JSON.parse(persistedSelection);
            
            // Check if selection is stale (older than 24 hours)
            const isStale = timestamp && (Date.now() - timestamp) > 24 * 60 * 60 * 1000;
            if (isStale) {
              devLog('⏰ Persisted user selection is stale, clearing...');
              localStorage.removeItem('selected-user-single-family');
            } else {
            
            // Find the persisted user in family data
            let restoredUser = null;
            if (isParent) {
              restoredUser = familyData.parents?.find(p => p.id === persistedUserId);
              if (restoredUser) {
                restoredUser = {
                  ...restoredUser,
                  isParent: true,
                  isOwner: restoredUser.isOwner,
                  type: 'parent',
                  originalParent: originalParent,
                  originalAuthUserId: originalAuthUserId
                };
              }
            } else {
              restoredUser = familyData.kids?.find(k => k.id === persistedUserId);
              if (restoredUser) {
                restoredUser = {
                  ...restoredUser,
                  isParent: false,
                  type: 'student',
                  originalParent: originalParent,
                  originalAuthUserId: originalAuthUserId
                };
              }
            }
            
            if (restoredUser) {
              devLog('✅ Restored persisted user selection:', restoredUser);
              setCurrentUser(restoredUser);
              currentUserToCache = restoredUser;
            } else {
              devWarn('⚠️ Persisted user not found in family data, clearing selection');
              localStorage.removeItem('selected-user-single-family');
            }
            } // End of else block for non-stale selections
          }
        } catch (error) {
          devWarn('Failed to restore persisted user selection:', error);
        }
      }

      // SAFEGUARD: Check if there's a real-user-context in localStorage (from testing mode)
      // This prevents parents from getting stuck in student contexts
      const realUserContext = localStorage.getItem('real-user-context');
      if (realUserContext && !currentUser && !currentUserToCache) {
        try {
          const parsedRealUser = JSON.parse(realUserContext);
          devLog('🔒 SAFEGUARD: Found real-user-context, restoring parent context:', parsedRealUser);
          
          // Find the real user in the family data to ensure they still exist
          const realUserInFamily = familyData.parents?.find(p => p.id === parsedRealUser.id) || 
                                   familyData.kids?.find(k => k.id === parsedRealUser.id);
          
          if (realUserInFamily) {
            const restoredUser = {
              ...realUserInFamily,
              isParent: parsedRealUser.isParent,
              type: parsedRealUser.type,
              isOwner: realUserInFamily.isOwner
            };
            setCurrentUser(restoredUser);
            currentUserToCache = restoredUser;
            
            // Clear the real-user-context since we've restored it
            localStorage.removeItem('real-user-context');
            localStorage.removeItem('testing-mode');
            localStorage.removeItem('testing-profile-id');
            localStorage.removeItem('mock-college-preferences');
            
            devLog('✅ SAFEGUARD: Successfully restored parent context and cleared testing flags');
          } else {
            devWarn('⚠️ SAFEGUARD: Real user not found in family data, clearing stale context');
            localStorage.removeItem('real-user-context');
          }
        } catch (error) {
          devError('🚫 SAFEGUARD: Error parsing real-user-context:', error);
          localStorage.removeItem('real-user-context');
        }
      }
      
      // Auto-set the current user to the family owner if not already set, is temporary, or no persisted selection was restored
      if ((!currentUser || currentUser.isTemporary) && !currentUserToCache && familyData.parents) {
        const owner = familyData.parents.find(p => p.authUserId === userId);
        if (owner) {
          devLog('Auto-setting current user to family owner:', owner);
          const newCurrentUser = {
            ...owner,
            isParent: true,
            isOwner: owner.isOwner,
            type: 'parent'
          };
          setCurrentUser(newCurrentUser);
          currentUserToCache = newCurrentUser;
        } else {
          // Check if user is a kid
          const loggedInKid = familyData.kids.find(k => k.authUserId === userId);
          if (loggedInKid) {
            devLog('Auto-setting current user to logged-in kid:', loggedInKid);
            const newCurrentUser = {
              ...loggedInKid,
              isParent: false,
              type: 'student'
            };
            setCurrentUser(newCurrentUser);
            currentUserToCache = newCurrentUser;
          } else if (!currentUser || currentUser.isTemporary) {
            // User is not found in either parents or kids
            // Only clear if it's temporary or not set
            devLog('User not found in family members, clearing temporary currentUser');
            setCurrentUser(null);
            currentUserToCache = null;
          }
        }
      }

      // Load family users for switching functionality
      const allFamilyUsers = [];
      
      // Add all parents
      if (familyData.parents) {
        familyData.parents.forEach(parent => {
          allFamilyUsers.push({
            ...parent,
            isParent: true,
            displayName: parent.name || 'Parent',
            type: 'parent'
          });
        });
      }
      
      // Add all kids
      if (familyData.kids) {
        familyData.kids.forEach(kid => {
          allFamilyUsers.push({
            ...kid,
            isParent: false,
            displayName: kid.name || 'Student',
            type: 'student'
          });
        });
      }
      
      setFamilyUsers(allFamilyUsers);
      devLog('👥 Family users loaded for switching:', allFamilyUsers.length);

      // Cache the data with more complete information to prevent data loss
      const cacheData = {
        cacheVersion: 2, // Increment this when data structure changes
        family: {
          id: familyData.id,
          name: familyData.name,
          emoji: familyData.emoji,
          parentName: familyData.parentName,
          parentAvatar: familyData.parentAvatar,
          parents: familyData.parents.map(p => ({
            id: p.id,
            name: p.name,
            avatar: p.avatar,
            isOwner: p.isOwner,
            authUserId: p.authUserId
          })),
          kids: familyData.kids.map(k => ({
            id: k.id,
            name: k.name,
            avatar: k.avatar,
            age: k.age,
            authUserId: k.authUserId
          }))
        },
        // Cache more complete data to preserve images and details
        activities: activitiesByUser,
        plans: plansByUser,
        patterns: patternsByKey,
        familyActivities: familyActivitiesData, // Keep full data including images
        familyCalendar: calendarData,
        recsIdeas: ideasData,
        familyTimeInvites: invitesData,
        kidAllowances: allowancesByKid,
        allowanceTransactions: transactionsByKid,
        allowanceJobs: jobsData,
        personalModules: personalModules,
        // Cache current user to restore properly
        currentUser: currentUserToCache
      };
      
      try {
        const cacheString = JSON.stringify(cacheData);
        const cacheSize = new Blob([cacheString]).size;
        const maxCacheSize = 5 * 1024 * 1024; // 5MB - increased from 1MB to handle family data
        
        if (cacheSize > maxCacheSize) {
          devWarn(`Family data cache too large (${Math.round(cacheSize / 1024)}KB), skipping cache`);
          // Clear any existing cache to prevent stale data
          localStorage.removeItem(getCacheKey(userId));
          localStorage.removeItem(getCacheTimestampKey(userId));
          return;
        }
        
        localStorage.setItem(getCacheKey(userId), cacheString);
        localStorage.setItem(getCacheTimestampKey(userId), Date.now().toString());
        devLog(`Family data cached successfully (${Math.round(cacheSize / 1024)}KB)`);
      } catch (cacheErr) {
        devWarn('Failed to cache family data:', cacheErr);
        // Handle quota exceeded
        if (cacheErr.name === 'QuotaExceededError') {
          Object.keys(localStorage).forEach(key => {
            if (key.includes('family-data-')) {
              localStorage.removeItem(key);
            }
          });
        }
      }
      } catch (err) {
        devError('Error loading family data:', err);
        setError(err.message);
        throw err; // Re-throw for deduplication wrapper
      } finally {
        // Don't touch loading state here - it's only for initial auth
        lastLoadTimeRef.current = now;
        isLoadingDataRef.current = false;
      }
    });
  };

  const createFamily = async (familyData) => {
    try {
      if (!user) {
        throw new Error('User must be authenticated to create a family');
      }
      
      const newFamily = await supabaseService.createFamily(familyData, user.id);
      setFamily(newFamily);
      
      return newFamily;
    } catch (err) {
      console.error('Error creating family:', err);
      setError(err.message);
      throw err;
    }
  };

  const addActivity = async (userId, activity) => {
    try {
      const newActivity = await supabaseService.addActivity(userId, activity);
      setActivities(prev => ({
        ...prev,
        [userId]: [...(prev[userId] || []), newActivity]
      }));
      return newActivity;
    } catch (err) {
      console.error('Error adding activity:', err);
      setError(err.message);
      throw err;
    }
  };

  const markActivityViewed = async (userId, activityId) => {
    try {
      await supabaseService.updateActivity(activityId, { is_new: false });
      setActivities(prev => ({
        ...prev,
        [userId]: prev[userId].map(act => 
          act.id === activityId ? { ...act, isNew: false } : act
        )
      }));
    } catch (err) {
      console.error('Error marking activity viewed:', err);
      setError(err.message);
    }
  };

  const updatePlan = async (userId, date, planData, notes = '') => {
    console.log('🔄 FamilyContext.updatePlan called:', { userId, date, planData, notes });
    
    try {
      // Handle both old format (plannedActivities array) and new format (object with big3)
      const isBig3Format = planData && typeof planData === 'object' && planData.big3;
      const isParent = currentUser?.isParent || planData?.userType === 'parent';
      
      console.log('📊 Plan format check:', { isBig3Format, isParent, currentUser: currentUser?.name });
      console.log('📋 Plan data received:', planData);
      console.log('📋 planData.big3:', planData.big3);
      
      const plan = {
        title: `Plan for ${date}`,
        description: notes,
        date,
        user_id: userId,
        user_type: isParent ? 'parent' : 'kid',
        // Fix: Don't put big3 array in activities - keep activities separate
        activities: isBig3Format ? null : planData, // Only use for legacy format
        big_3: isBig3Format ? planData.big3 : null,
        // Legacy support
        kid_id: !isParent ? userId : null
      };
      
      console.log('💾 Plan object to save:', plan);
      console.log('🏠 Family ID:', family?.id);
      
      await supabaseService.savePlan(plan);
      console.log('✅ Plan saved to Supabase successfully');
      
      // Update local state to match expected structure: plans[userId][date]
      setPlans(prev => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [date]: isBig3Format ? planData : planData
        }
      }));
      
      console.log('📝 Updated local plans state for user:', userId, 'date:', date);

      // Add to calendar if needed (only for old format with activities array)
      const activitiesToProcess = isBig3Format ? [] : (Array.isArray(planData) ? planData : []);
      for (const activity of activitiesToProcess) {
        if (activity.addToCalendar) {
          await addActivityToCalendar({
            type: isParent ? 'parent-activity' : 'kid-activity',
            kidId: !isParent ? userId : null,
            parentId: isParent ? userId : null,
            kidName: !isParent ? family?.kids.find(k => k.id === userId)?.name : null,
            parentName: isParent ? family?.parents.find(p => p.id === userId)?.name : null,
            activityId: activity.activityId,
            activityName: activity.activityName,
            date,
            time: activity.timeSlot,
            category: activity.category,
            energyLevel: activity.energyLevel,
            notes
          });
        }
      }
    } catch (err) {
      console.error('Error updating plan:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateActivityStatus = async (kidId, date, activityId, status) => {
    const planKey = `${kidId}_${date}`;
    setPlans(prev => ({
      ...prev,
      [planKey]: prev[planKey].map(act =>
        act.activityId === activityId ? { ...act, status } : act
      )
    }));

    // Update patterns
    if (status === 'shifted' || status === 'cancelled') {
      const patternKey = `${kidId}_${activityId}`;
      setPatterns(prev => ({
        ...prev,
        [patternKey]: {
          ...prev[patternKey],
          shiftCount: (prev[patternKey]?.shiftCount || 0) + (status === 'shifted' ? 1 : 0),
          cancelCount: (prev[patternKey]?.cancelCount || 0) + (status === 'cancelled' ? 1 : 0),
          lastShifted: status === 'shifted' ? new Date() : prev[patternKey]?.lastShifted
        }
      }));
      
      // Save pattern to Supabase
      await supabaseService.savePattern({
        name: patternKey,
        activities: [{ kidId, activityId, status }]
      });
    }
  };

  const addFamilyActivity = async (activityData) => {
    try {
      console.log('📝 Adding family activity:', activityData);
      
      // Ensure we have a family ID
      if (!family?.id) {
        throw new Error('No family found');
      }

      // Ensure image is stored as a URL string
      const cleanedData = {
        ...activityData,
        
        image: activityData.image || null,
        votes: {},
        status: activityData.status || 'Unscheduled'
      };

      const { data, error } = await supabase
        .from('family_activities')
        .insert([cleanedData])
        .select()
        .single();

      if (error) {
        console.error('❌ Error adding family activity:', error);
        throw error;
      }

      console.log('✅ Family activity added:', data);
      setFamilyActivities(prev => [...prev, data]);
      
      // Invalidate cache so next reload gets fresh data
      if (user?.id) {
        localStorage.removeItem(`family-data-${user.id}`);
        localStorage.removeItem(`family-data-timestamp-${user.id}`);
        console.log('🗑️ Cache invalidated after adding activity');
      }
      
      return data;
    } catch (err) {
      console.error('❌ Error adding family activity:', err);
      throw err;
    }
  };

  const voteOnFamilyActivity = async (activityId, userId, vote) => {
    try {
      console.log('🗳️ Voting on activity:', { activityId, userId, vote });
      
      // Find the current activity
      const activity = familyActivities.find(act => act.id === activityId);
      if (!activity) {
        console.error('❌ Activity not found in familyActivities:', { activityId, familyActivitiesCount: familyActivities.length });
        throw new Error('Activity not found');
      }
      
      console.log('🗳️ Found activity:', {
        id: activity.id,
        title: activity.title,
        status: activity.status,
        confirmed_for_calendar: activity.confirmed_for_calendar,
        current_rsvps: activity.rsvps,
        current_ratings: activity.ratings
      });
      
      // Determine if this is a rating (0-10) or RSVP (yes/no) based on activity status
      // Match the logic used in FamilyActivities.jsx for consistency
      const isScheduled = activity.confirmed_for_calendar === true;
      const voteField = isScheduled ? 'rsvps' : 'ratings';
      
      console.log(`🗳️ Vote logic: isScheduled=${isScheduled}, voteField=${voteField}`);
      console.log(`🗳️ Current activity ${voteField}:`, activity[voteField]);
      const updatedVotes = { ...(activity[voteField] || {}), [userId]: vote };
      console.log(`🗳️ Updated ${voteField}:`, updatedVotes);
      
      // Update in database first
      const updateData = { [voteField]: updatedVotes };
      const updatedActivity = await supabaseService.updateFamilyActivity(activityId, updateData);
      console.log('🗳️ Database update response:', updatedActivity);
      
      // Update local state with the returned data, ensuring votes are preserved
      setFamilyActivities(prev => {
        const updated = prev.map(act => 
          act.id === activityId 
            ? { ...act, [voteField]: updatedVotes }
            : act
        );
        console.log('🗳️ Updated familyActivities state:', updated.find(a => a.id === activityId)?.votes);
        return updated;
      });
      
      console.log('✅ Vote saved successfully');
    } catch (err) {
      console.error('❌ Error saving vote:', err);
      setError(err.message);
      throw err;
    }
  };

  const rateActivity = async (activityId, userId, rating) => {
    try {
      console.log('⭐ Rating activity:', { activityId, userId, rating });
      
      // Validate rating is between 0-10
      if (rating < 0 || rating > 10) {
        throw new Error('Rating must be between 0 and 10');
      }
      
      // Find the current activity
      const activity = familyActivities.find(act => act.id === activityId);
      if (!activity) {
        throw new Error('Activity not found');
      }
      
      console.log('⭐ Current activity ratings:', activity.ratings);
      const updatedRatings = { ...(activity.ratings || {}), [userId]: rating };
      console.log('⭐ Updated ratings:', updatedRatings);
      
      // Update in database first
      const updatedActivity = await supabaseService.updateFamilyActivity(activityId, { ratings: updatedRatings });
      console.log('⭐ Database update response:', updatedActivity);
      
      // Update local state
      setFamilyActivities(prev => {
        const updated = prev.map(act => 
          act.id === activityId 
            ? { ...act, ratings: updatedRatings }
            : act
        );
        console.log('⭐ Updated familyActivities state:', updated.find(a => a.id === activityId)?.ratings);
        return updated;
      });
      
      console.log('✅ Rating saved successfully');
    } catch (err) {
      console.error('❌ Error saving rating:', err);
      setError(err.message);
      throw err;
    }
  };

  const restoreActivityImages = async () => {
    try {
      console.log('🔧 Starting automated image restoration...');
      
      if (!family?.id) {
        throw new Error('No family found');
      }

      // Check if we have cached image data first
      const cachedImages = getCachedActivityImages();
      if (cachedImages) {
        console.log('🚀 Using cached activity images');
        
        // Apply cached images to activities that don't have them
        const updates = [];
        const { data: activities, error: activitiesError } = await supabase
          .from('family_activities')
          .select('id, image')
          // family_id filter removed for single-family architecture
          .is('image', null);

        if (!activitiesError && activities.length > 0) {
          for (const activity of activities) {
            if (cachedImages[activity.id]) {
              updates.push({
                id: activity.id,
                image: cachedImages[activity.id]
              });
            }
          }
          
          if (updates.length > 0) {
            console.log(`🖼️ Applying ${updates.length} cached images`);
            // Apply updates in batches
            for (const update of updates) {
              await supabase
                .from('family_activities')
                .update({ image: update.image })
                .eq('id', update.id);
            }
          }
        }
        
        return { restored: updates.length, total: activities?.length || 0, cached: true };
      }

      // Get all activities with NULL images for this family
      const { data: activities, error: activitiesError } = await supabase
        .from('family_activities')
        .select('id, title, created_at, image')
        .is('image', null)
        .order('created_at', { ascending: false });

      if (activitiesError) throw activitiesError;

      if (activities.length === 0) {
        console.log('✅ No activities need image restoration');
        return { restored: 0, total: 0 };
      }

      console.log(`📋 Found ${activities.length} activities with missing images`);

      // Get all files from the family folder in storage (single-family architecture)
      const { data: files, error: filesError } = await supabase.storage
        .from('family-images')
        .list('single-family', { 
          limit: 1000, 
          sortBy: { column: 'created_at', order: 'desc' } 
        });

      if (filesError) throw filesError;

      console.log(`📁 Found ${files.length} files in family storage folder`);

      let restoredCount = 0;
      const updates = [];

      for (const activity of activities) {
        // Try to find matching file by creation time proximity
        const activityTime = new Date(activity.created_at);
        
        // Look for files created within 24 hours of the activity
        const matchingFiles = files.filter(file => {
          if (!file.created_at) return false;
          
          const fileTime = new Date(file.created_at);
          const timeDiff = Math.abs(fileTime.getTime() - activityTime.getTime());
          const hoursDiff = timeDiff / (1000 * 60 * 60);
          
          return hoursDiff <= 24; // Within 24 hours
        });

        if (matchingFiles.length > 0) {
          // Use the file with the closest creation time
          const bestMatch = matchingFiles.reduce((closest, current) => {
            const currentDiff = Math.abs(new Date(current.created_at).getTime() - activityTime.getTime());
            const closestDiff = Math.abs(new Date(closest.created_at).getTime() - activityTime.getTime());
            return currentDiff < closestDiff ? current : closest;
          });

          const imageUrl = supabase.storage
            .from('family-images')
            .getPublicUrl(`single-family/${bestMatch.name}`)
            .data.publicUrl;

          updates.push({
            id: activity.id,
            title: activity.title,
            imageUrl,
            fileName: bestMatch.name
          });

          console.log(`🔗 Matched "${activity.title}" with file: ${bestMatch.name}`);
        } else {
          console.log(`❌ No matching file found for "${activity.title}"`);
        }
      }

      // Batch update the database
      if (updates.length > 0) {
        console.log(`💾 Updating ${updates.length} activities...`);
        
        for (const update of updates) {
          const { error: updateError } = await supabase
            .from('family_activities')
            .update({ image: update.imageUrl })
            .eq('id', update.id);

          if (updateError) {
            console.error(`❌ Failed to update ${update.title}:`, updateError);
          } else {
            restoredCount++;
            console.log(`✅ Restored image for "${update.title}"`);
          }
        }

        // Cache the image mappings for future use
        const imageMap = {};
        for (const update of updates) {
          imageMap[update.id] = update.imageUrl;
        }
        cacheActivityImages('single-family', imageMap);

        // Refresh the family activities state
        await refreshFamilyData();
      }

      console.log(`\n🎉 Restoration complete!`);
      console.log(`📊 Restored ${restoredCount} out of ${activities.length} activities`);
      
      return {
        restored: restoredCount,
        total: activities.length,
        updates
      };

    } catch (error) {
      console.error('❌ Error in automated restoration:', error);
      setError(error.message);
      throw error;
    }
  };

  const scheduleToCalendar = async (activity, date, time) => {
    try {
      // First, update the family activity with new confirmation system
      await supabaseService.updateFamilyActivity(activity.id, { 
        status: 'has_dates',
        start_date: date,
        end_date: null, // Single day scheduling
        date, // Keep legacy date field for backward compatibility
        time,
        confirmed_for_calendar: true // Mark as confirmed for calendar
      });
      
      // Update local state for family activities
      setFamilyActivities(prev => prev.map(act => 
        act.id === activity.id ? { 
          ...act, 
          status: 'has_dates', 
          start_date: date,
          end_date: null,
          date, 
          time,
          confirmed_for_calendar: true
        } : act
      ));

      // Remove any potential dates from calendar
      const potentialEvents = familyCalendar.filter(event => 
        event.type === 'potential-activity' && 
        event.familyActivityId === activity.id
      );
      
      for (const event of potentialEvents) {
        await removeCalendarEvent(event.calendarId);
      }

      // No need to create separate calendar event since the activity itself
      // will now appear in the calendar with confirmed_for_calendar: true

      // Notify all family members about confirmed plan
      for (const member of family.kids) {
        await notificationManager.sendNotification(
          member.id,
          'confirmedFamilyPlan',
          {
            activity: activity.title,
            datetime: `${date} at ${time}`
          }
        );
      }
    } catch (err) {
      console.error('Error scheduling to calendar:', err);
      setError(err.message);
      throw err;
    }
  };

  const addPotentialDatesToFamilyActivity = async (activityId, potential_dates) => {
    try {
      const activity = familyActivities.find(act => act.id === activityId);
      
      await supabaseService.updateFamilyActivity(activityId, { 
        potential_dates: potential_dates 
      });
      
      setFamilyActivities(prev => prev.map(act => 
        act.id === activityId 
          ? { ...act, potential_dates }
          : act
      ));

      // Add potential dates to calendar
      for (const dateInfo of potential_dates) {
        await addActivityToCalendar({
          type: 'potential-activity',
          familyActivityId: activityId,
          title: activity?.title || 'Unknown Activity',
          description: activity?.description || '',
          date: dateInfo.date,
          time: dateInfo.timeRange || 'TBD',
          isPotential: true
        });
      }
    } catch (err) {
      console.error('Error adding potential dates:', err);
      setError(err.message);
      throw err;
    }
  };

  const editActivity = async (userId, activityId, updatedActivity) => {
    try {
      const updatedData = await supabaseService.updateActivity(activityId, updatedActivity);
      setActivities(prev => ({
        ...prev,
        [userId]: prev[userId].map(activity =>
          activity.id === activityId ? updatedData : activity
        )
      }));
      return updatedData;
    } catch (err) {
      console.error('Error editing activity:', err);
      setError(err.message);
      throw err;
    }
  };

  const deleteActivity = async (userId, activityId) => {
    try {
      await supabaseService.deleteActivity(activityId);
      setActivities(prev => ({
        ...prev,
        [userId]: prev[userId].filter(activity => activity.id !== activityId)
      }));
    } catch (err) {
      console.error('Error deleting activity:', err);
      setError(err.message);
      throw err;
    }
  };

  const editFamilyActivity = async (activityId, updates) => {
    try {
      // Ensure image is stored as a URL string
      const cleanedUpdates = {
        ...(updates || {}),
        image: updates?.image?.url || null
      };

      // Never allow updating the creator when editing
      delete cleanedUpdates.created_by;

      const { data, error } = await supabase
        .from('family_activities')
        .update(cleanedUpdates)
        .eq('id', activityId)
        .select()
        .single();

      if (error) throw error;

      setFamilyActivities(prev =>
        prev.map(activity =>
          activity.id === activityId ? { ...activity, ...cleanedUpdates, ...data } : activity
        )
      );
      
      // Invalidate cache so changes are immediately visible
      if (user?.id) {
        localStorage.removeItem(`family-data-${user.id}`);
        localStorage.removeItem(`family-data-timestamp-${user.id}`);
        console.log('🗑️ Cache invalidated after editing activity');
      }
      
      return { ...updates, ...data };
    } catch (err) {
      console.error('Error updating family activity:', err);
      throw err;
    }
  };

  const updateFamilyActivity = async (activityId, updates) => {
    try {
      console.log('📝 Updating family activity:', { activityId, updates });
      
      // Get the current activity to preserve existing data
      const currentActivity = familyActivities.find(act => act.id === activityId);
      if (!currentActivity) {
        throw new Error(`Activity with ID ${activityId} not found`);
      }
      
      // Clean up updates to ensure image is a string URL and preserve existing values
      const cleanedUpdates = {
        ...updates,
        // Preserve image if not being updated
        image: updates.image !== undefined ? (updates.image?.url || updates.image || null) : currentActivity.image,
        // Preserve date fields if not being explicitly updated  
        date: updates.date !== undefined ? updates.date : currentActivity.date,
        start_date: updates.start_date !== undefined ? updates.start_date : currentActivity.start_date,
        end_date: updates.end_date !== undefined ? updates.end_date : currentActivity.end_date,
        time: updates.time !== undefined ? updates.time : currentActivity.time
      };

      // Remove any fields that don't exist in the database or shouldn't be updated
      delete cleanedUpdates.created_by_name;
      delete cleanedUpdates.created_by; // Never update the original creator

      const updatedActivity = await supabaseService.updateFamilyActivity(activityId, cleanedUpdates);
      
      setFamilyActivities(prev => 
        prev.map(activity => 
          activity.id === activityId ? updatedActivity : activity
        )
      );

      return updatedActivity;
    } catch (error) {
      console.error('Error updating family activity:', error);
      throw error;
    }
  };

  const cancelFamilyActivity = async (activityId) => {
    try {
      await supabaseService.deleteFamilyActivity(activityId);
      setFamilyActivities(prev => prev.filter(activity => activity.id !== activityId));
      
      // Invalidate cache so changes are immediately visible
      if (user?.id) {
        localStorage.removeItem(`family-data-${user.id}`);
        localStorage.removeItem(`family-data-timestamp-${user.id}`);
        console.log('🗑️ Cache invalidated after deleting activity');
      }
    } catch (err) {
      console.error('Error cancelling family activity:', err);
      setError(err.message);
      throw err;
    }
  };

  const archiveFamilyActivity = async (activityId) => {
    try {
      console.log('📁 Archiving family activity:', activityId);
      
      // Update activity status to archived
      const updatedActivity = await supabaseService.updateFamilyActivity(activityId, { 
        status: 'archived',
        archived_date: new Date().toISOString()
      });
      
      // Update local state
      setFamilyActivities(prev => 
        prev.map(activity => 
          activity.id === activityId 
            ? { ...activity, status: 'archived', archived_date: new Date().toISOString() }
            : activity
        )
      );

      // Invalidate cache so changes are immediately visible
      if (user?.id) {
        localStorage.removeItem(`family-data-${user.id}`);
        localStorage.removeItem(`family-data-timestamp-${user.id}`);
        console.log('🗑️ Cache invalidated after archiving activity');
      }
      
      console.log('✅ Activity archived successfully');
      return updatedActivity;
    } catch (err) {
      console.error('❌ Error archiving family activity:', err);
      setError(err.message);
      throw err;
    }
  };

  const unarchiveFamilyActivity = async (activityId) => {
    try {
      console.log('📤 Unarchiving family activity:', activityId);
      
      // Update activity status back to idea or has_dates based on whether it has dates
      const activity = familyActivities.find(act => act.id === activityId);
      const newStatus = (activity?.start_date || activity?.date) ? 'has_dates' : 'idea';
      
      const updatedActivity = await supabaseService.updateFamilyActivity(activityId, { 
        status: newStatus,
        archived_date: null
      });
      
      // Update local state
      setFamilyActivities(prev => 
        prev.map(activity => 
          activity.id === activityId 
            ? { ...activity, status: newStatus, archived_date: null }
            : activity
        )
      );

      // Invalidate cache so changes are immediately visible
      if (user?.id) {
        localStorage.removeItem(`family-data-${user.id}`);
        localStorage.removeItem(`family-data-timestamp-${user.id}`);
        console.log('🗑️ Cache invalidated after unarchiving activity');
      }
      
      console.log('✅ Activity unarchived successfully');
      return updatedActivity;
    } catch (err) {
      console.error('❌ Error unarchiving family activity:', err);
      setError(err.message);
      throw err;
    }
  };

  // Mark activity as completed
  const markActivityCompleted = async (activityId, completionData) => {
    try {
      console.log('✅ Marking activity as completed:', activityId, completionData);
      
      // Add auto-archive status for non-favorites
      const activity = familyActivities.find(a => a.id === activityId);
      const shouldArchive = !activity?.is_favorite;
      
      const finalData = {
        ...completionData,
        status: shouldArchive ? 'archived' : activity?.status || 'completed'
      };
      
      const updatedActivity = await supabaseService.markActivityCompleted(activityId, finalData);
      
      // Update local state
      setFamilyActivities(prev => 
        prev.map(activity => 
          activity.id === activityId 
            ? { ...activity, ...finalData }
            : activity
        )
      );
      
      // Invalidate cache so changes are immediately visible
      if (user?.id) {
        localStorage.removeItem(`family-data-${user.id}`);
        localStorage.removeItem(`family-data-timestamp-${user.id}`);
        console.log('🗑️ Cache invalidated after marking activity as completed');
      }
      
      console.log(`✅ Activity marked as completed ${shouldArchive ? 'and archived' : ''} successfully`);
      return updatedActivity;
    } catch (error) {
      console.error('❌ Error marking activity as completed:', error);
      throw error;
    }
  };

  // Toggle activity favorite status
  const toggleActivityFavorite = async (activityId, isFavorite) => {
    try {
      console.log(`⭐ Toggling favorite for activity ${activityId} to ${isFavorite}`);
      
      const updatedActivity = await supabaseService.toggleActivityFavorite(activityId, isFavorite);
      
      // If unmarking favorite and activity is completed, auto-archive it
      const activity = familyActivities.find(a => a.id === activityId);
      if (!isFavorite && activity?.completed_date) {
        await supabaseService.updateFamilyActivity(activityId, { status: 'archived' });
        updatedActivity.status = 'archived';
      } else if (isFavorite && activity?.status === 'archived' && activity?.completed_date) {
        // If marking as favorite and it's archived+completed, unarchive it
        await supabaseService.updateFamilyActivity(activityId, { status: 'completed' });
        updatedActivity.status = 'completed';
      }
      
      // Update local state
      setFamilyActivities(prev => 
        prev.map(activity => 
          activity.id === activityId 
            ? { ...activity, is_favorite: isFavorite, status: updatedActivity.status }
            : activity
        )
      );
      
      // Invalidate cache
      if (user?.id) {
        localStorage.removeItem(`family-data-${user.id}`);
        localStorage.removeItem(`family-data-timestamp-${user.id}`);
        console.log('🗑️ Cache invalidated after toggling favorite');
      }
      
      console.log('✅ Activity favorite status updated successfully');
      return updatedActivity;
    } catch (error) {
      console.error('❌ Error toggling activity favorite:', error);
      throw error;
    }
  };

  const autoArchivePastActivities = async () => {
    try {
      console.log('🕐 Running auto-archive for past activities...');
      
      if (!family?.id) {
        console.log('❌ No family found for auto-archiving');
        return { archived: 0, total: 0 };
      }

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      
      // Find activities that:
      // 1. Have past dates (more than 30 days ago to avoid accidentally archiving recent past events)
      // 2. Are not already archived
      // 3. Are not confirmed for calendar (let calendar events stay visible)
      const activitiesToArchive = familyActivities.filter(activity => {
        if (activity.status === 'archived') return false;
        if (activity.confirmed_for_calendar) return false;
        
        const activityDate = activity.start_date || activity.date;
        if (!activityDate) return false;
        
        try {
          const eventDate = new Date(activityDate);
          return eventDate < thirtyDaysAgo;
        } catch (error) {
          console.warn('Error parsing activity date for auto-archive:', activityDate, error);
          return false;
        }
      });

      if (activitiesToArchive.length === 0) {
        console.log('✅ No activities need auto-archiving');
        return { archived: 0, total: familyActivities.length };
      }

      console.log(`📁 Found ${activitiesToArchive.length} activities to auto-archive`);
      
      let archivedCount = 0;
      const autoArchiveDate = new Date().toISOString();
      
      // Archive activities in batches
      for (const activity of activitiesToArchive) {
        try {
          await supabaseService.updateFamilyActivity(activity.id, {
            status: 'archived',
            archived_date: autoArchiveDate,
            auto_archived: true
          });
          
          archivedCount++;
          console.log(`✅ Auto-archived "${activity.title}"`);
        } catch (error) {
          console.error(`❌ Failed to auto-archive "${activity.title}":`, error);
        }
      }
      
      // Update local state for all archived activities
      if (archivedCount > 0) {
        setFamilyActivities(prev => 
          prev.map(activity => 
            activitiesToArchive.find(a => a.id === activity.id)
              ? { ...activity, status: 'archived', archived_date: autoArchiveDate, auto_archived: true }
              : activity
          )
        );
        
        // Invalidate cache
        if (user?.id) {
          localStorage.removeItem(`family-data-${user.id}`);
          localStorage.removeItem(`family-data-timestamp-${user.id}`);
          console.log('🗑️ Cache invalidated after auto-archiving');
        }
      }
      
      console.log(`🎉 Auto-archive complete! Archived ${archivedCount} out of ${activitiesToArchive.length} activities`);
      
      return {
        archived: archivedCount,
        total: activitiesToArchive.length,
        activities: activitiesToArchive.slice(0, archivedCount)
      };
      
    } catch (error) {
      console.error('❌ Error in auto-archive process:', error);
      throw error;
    }
  };

  const updateFamilyActivityStatus = async (activityId, status) => {
    try {
      const activity = familyActivities.find(act => act.id === activityId);
      
      await supabaseService.updateFamilyActivity(activityId, { 
        status,
        is_scheduled: status === 'onCalendar'
      });
      
      setFamilyActivities(prev =>
        prev.map(act =>
          act.id === activityId ? { ...act, status, isScheduled: status === 'onCalendar' } : act
        )
      );
      
      // Add to calendar if marking as "On Calendar"
      if (status === 'onCalendar' && activity) {
        const alreadyScheduled = familyCalendar.some(
          event => event.familyActivityId === activityId && event.type === 'family-activity'
        );
        
        if (!alreadyScheduled) {
          await addActivityToCalendar({
            type: 'family-activity',
            familyActivityId: activityId,
            title: activity.title,
            description: activity.description,
            date: activity.date || activity.start_date || new Date().toISOString().split('T')[0],
            time: activity.time || 'TBD',
            status: 'onCalendar'
          });
        }
      }
    } catch (err) {
      console.error('Error updating Family Event status:', err);
      setError(err.message);
      throw err;
    }
  };

  const addActivityToCalendar = async (activityData) => {
    try {
      const event = await supabaseService.addCalendarEvent({
        title: activityData.title || activityData.activityName,
        description: activityData.description,
        category: activityData.category,
        location: activityData.location,
        date: activityData.date,
        time: activityData.time,
        type: activityData.type,
        activities: activityData.activities || [],
        meal_plan: activityData.mealPlan,
        family_activity_id: activityData.familyActivityId,
        participants: activityData.participants || [],
        estimated_duration: activityData.estimatedDuration,
        min_participants: activityData.minParticipants || 2,
        max_participants: activityData.maxParticipants,
        suggested_ages: activityData.suggestedAges,
        materials_needed: activityData.materialsNeeded || [],
        cost_estimate: activityData.costEstimate,
        difficulty_level: activityData.difficultyLevel,
        weather_requirements: activityData.weatherRequirements,
        best_time_of_day: activityData.bestTimeOfDay,
        season_preference: activityData.seasonPreference,
        notes: activityData.notes,
        image: activityData.image,
        status: activityData.status || 'scheduled',
        created_by: activityData.created_by
      });
      
      setFamilyCalendar(prev => [...prev, {
        ...event,
        calendarId: event.id,
        ...activityData
      }]);

      // Send notification for confirmed family plans
      if (activityData.type === 'family-activity' && event.date && event.time && event.time !== 'TBD') {
        try {
          const allMembers = [
            { id: 'parent', name: family.parentName },
            ...family.kids
          ];
          
          for (const member of allMembers) {
            await notificationManager.sendNotification(
              member.id,
              'confirmedFamilyPlans',
              {
                activity: event.title,
                datetime: `${event.date} ${event.time}`
              }
            );
          }
        } catch (error) {
          console.error('Error sending confirmed plan notifications:', error);
        }
      }
      
      return event;
    } catch (err) {
      console.error('Error adding to calendar:', err);
      setError(err.message);
      throw err;
    }
  };

  const editCalendarEvent = async (calendarId, updatedEvent) => {
    try {
      // Ensure image is stored as a URL string
      const cleanedUpdates = {
        ...updatedEvent,
        image: updatedEvent.image?.url || updatedEvent.image || null
      };

      await supabaseService.updateCalendarEvent(calendarId, cleanedUpdates);
      setFamilyCalendar(prev =>
        prev.map(event =>
          event.calendarId === calendarId ? { ...event, ...cleanedUpdates } : event
        )
      );
    } catch (err) {
      console.error('Error editing calendar event:', err);
      setError(err.message);
      throw err;
    }
  };

  const removeCalendarEvent = async (calendarId) => {
    try {
      if (!calendarId) {
        console.error('No calendar ID provided for removal');
        return;
      }

      // Find the event to be removed
      const eventToRemove = familyCalendar.find(event => event.calendarId === calendarId);
      
      // Delete the calendar event
      await supabaseService.deleteCalendarEvent(calendarId);
      
      // If this was a family activity, update its status
      if (eventToRemove?.familyActivityId) {
        await supabaseService.updateFamilyActivity(eventToRemove.familyActivityId, {
          status: 'idea',
          is_scheduled: false
        });
        
        // Update the family activity in state
        setFamilyActivities(prev =>
          prev.map(activity =>
            activity.id === eventToRemove.familyActivityId
              ? { ...activity, status: 'idea', isScheduled: false }
              : activity
          )
        );
      }
      
      // Remove from calendar state
      setFamilyCalendar(prev =>
        prev.filter(event => event.calendarId !== calendarId)
      );
    } catch (err) {
      console.error('Error removing calendar event:', err);
      setError(err.message);
      throw err;
    }
  };

  // Ideas & Opportunities functions
  const addIdea = async (ideaData) => {
    try {
      console.log('🔍 FamilyContext addIdea called with:', {
        ideaData,
        familyId: family?.id,
        currentUserId: currentUser?.id,
        hasFamily: !!family,
        hasCurrentUser: !!currentUser
      });

      if (!family?.id) {
        throw new Error('No family found. Please refresh the page and try again.');
      }

      if (!currentUser?.id) {
        throw new Error('No current user found. Please refresh the page and try again.');
      }

      const newIdea = await supabaseService.addIdea(
        ideaData,
        currentUser.id
      );
      
      setRecsIdeas(prev => [...prev, {
        ...newIdea,
        addedBy: currentUser?.isParent ? family.parentName : currentUser?.name,
        status: 'new',
        kidResponses: {}
      }]);

      // Notify all family members except creator about new idea
      const creatorId = currentUser?.id;
      for (const member of family.kids) {
        if (member.id !== creatorId) {
          await notificationManager.sendNotification(
            member.id,
            'newIdea',
            {
              title: ideaData.title,
              category: ideaData.category || 'idea'
            }
          );
        }
      }
      
      return newIdea;
    } catch (err) {
      console.error('Error adding idea:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateIdeaStatus = async (ideaId, kidId, status, enhancedData = {}) => {
    try {
      // If enhanced data is provided, use the new enhanced response system
      if (enhancedData && Object.keys(enhancedData).length > 0) {
        const responseData = {
          responseType: status,
          helpRequested: enhancedData.helpRequested || [],
          comment: enhancedData.comment || '',
          timing: enhancedData.timing || '',
          ...enhancedData
        };
        
        await supabaseService.updateEnhancedResponse(ideaId, kidId, responseData);
        
        setRecsIdeas(prev =>
          prev.map(idea =>
            idea.id === ideaId
              ? {
                  ...idea,
                  kidResponses: {
                    ...idea.kidResponses,
                    [kidId]: { status, timestamp: new Date().toISOString(), ...enhancedData }
                  },
                  enhancedResponses: {
                    ...idea.enhancedResponses,
                    [kidId]: responseData
                  }
                }
              : idea
          )
        );
      } else {
        // Legacy response handling for backward compatibility
        const idea = recsIdeas.find(i => i.id === ideaId);
        const currentVotes = idea.votes || {};
        const updatedVotes = {
          ...currentVotes,
          [kidId]: status
        };
        
        await supabaseService.updateIdea(ideaId, { votes: updatedVotes });
        
        setRecsIdeas(prev =>
          prev.map(idea =>
            idea.id === ideaId
              ? {
                  ...idea,
                  kidResponses: {
                    ...idea.kidResponses,
                    [kidId]: { status, date: new Date() }
                  }
                }
              : idea
          )
        );
      }
    } catch (err) {
      console.error('Error updating idea status:', err);
      setError(err.message);
      throw err;
    }
  };

  const editIdea = async (ideaId, updatedIdea) => {
    try {
      console.log('🔄 Editing idea with data:', updatedIdea);
      
      const dbResult = await supabaseService.updateIdea(ideaId, updatedIdea);
      console.log('✅ Database update result:', dbResult);
      
      // Transform the updated data back to frontend format (same as getIdeas transformation)
      const transformedUpdate = {
        ...updatedIdea,
        targetKids: updatedIdea.targetKids, // Keep targetKids in camelCase for frontend
        createdBy: updatedIdea.created_by || updatedIdea.createdBy,
        linkData: updatedIdea.link_metadata || updatedIdea.linkData,
        estimatedTime: updatedIdea.estimated_time || updatedIdea.estimatedTime,
        materialsNeeded: updatedIdea.materials_needed || updatedIdea.materialsNeeded,
        isAudiobook: updatedIdea.is_audiobook || updatedIdea.isAudiobook,
        whyRecommended: updatedIdea.why_recommended || updatedIdea.whyRecommended,
        rottenTomatoesCritic: updatedIdea.rotten_tomatoes_critic || updatedIdea.rottenTomatoesCritic,
        rottenTomatoesAudience: updatedIdea.rotten_tomatoes_audience || updatedIdea.rottenTomatoesAudience,
        googleAudienceScore: updatedIdea.google_audience_score || updatedIdea.googleAudienceScore,
        trailerUrl: updatedIdea.trailer_url || updatedIdea.trailerUrl,
        releaseYear: updatedIdea.release_year || updatedIdea.releaseYear,
        isTV: updatedIdea.is_tv || updatedIdea.isTV
      };
      
      console.log('🔄 Updating local state with transformed data:', transformedUpdate);
      
      setRecsIdeas(prev =>
        prev.map(idea =>
          idea.id === ideaId ? { ...idea, ...transformedUpdate } : idea
        )
      );
    } catch (err) {
      console.error('Error editing idea:', err);
      setError(err.message);
      throw err;
    }
  };

  const deleteIdea = async (ideaId) => {
    try {
      await supabaseService.deleteIdea(ideaId);
      setRecsIdeas(prev =>
        prev.filter(idea => idea.id !== ideaId)
      );
    } catch (err) {
      console.error('Error deleting idea:', err);
      setError(err.message);
      throw err;
    }
  };

  // 1:1 Invites functions
  const addFamilyTimeInvite = async (inviteData) => {
    try {
      const newInvite = await supabaseService.addFamilyTimeInvite(
        inviteData,
        currentUser?.id
      );
      
      setFamilyTimeInvites(prev => [...prev, {
        ...newInvite,
        createdBy: currentUser?.isParent ? family.parentName : currentUser?.name
      }]);
      
      // Send notification to invitee
      if (inviteData.invitee) {
        await notificationManager.sendNotification(
          inviteData.invitee,
          'familyTimeInvite',
          {
            inviterName: currentUser?.isParent ? family.parentName : currentUser?.name,
            activity: inviteData.activityIdea,
            time: inviteData.preferredTime || 'soon'
          }
        );
      }
      
      return newInvite;
    } catch (err) {
      console.error('Error adding 1:1 invite:', err);
      setError(err.message);
      throw err;
    }
  };

  const respondToInvite = async (inviteId, kidId, response, message = '') => {
    try {
      await supabaseService.updateFamilyTimeInvite(inviteId, {
        status: response,
        kid_response: message,
        response_date: new Date(),
        responding_kid: kidId
      });
      
      // Find the invite to get its details
      const invite = familyTimeInvites.find(inv => inv.id === inviteId);
      
      // If accepted, add to family calendar
      if (response === 'accepted' && invite) {
        const kidMember = family?.kids?.find(k => k.id === kidId);
        const parentMember = family?.parents?.[0]; // Assuming parent is the inviter
        
        // Parse the proposed date
        let eventDate, eventTime;
        try {
          const proposedDateTime = new Date(invite.proposedDate);
          eventDate = proposedDateTime.toISOString().split('T')[0];
          eventTime = proposedDateTime.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          });
        } catch (dateError) {
          // Fallback if date parsing fails
          eventDate = new Date().toISOString().split('T')[0];
          eventTime = 'TBD';
          console.warn('Could not parse invite date:', invite.proposedDate);
        }
        
        try {
          await addActivityToCalendar({
            type: 'family-time',
            title: invite.title || '1:1 Time',
            description: invite.description || `One-on-one time between ${parentMember?.name || 'Parent'} and ${kidMember?.name || 'Kid'}${message ? `\n\nNote from ${kidMember?.name}: "${message}"` : ''}`,
            date: eventDate,
            time: eventTime,
            location: invite.location,
            category: 'family_time',
            participants: [
              parentMember?.id || 'parent',
              kidId
            ],
            min_participants: 2,
            max_participants: 2,
            family_time_invite: inviteId,
            status: 'scheduled'
          });
          
          console.log('✅ Added accepted family time invite to calendar');
        } catch (calendarError) {
          console.warn('Could not add to calendar:', calendarError);
          // Don't throw here - the response was still successful
        }
      }
      
      setFamilyTimeInvites(prev =>
        prev.map(invite =>
          invite.id === inviteId
            ? {
                ...invite,
                status: response,
                kidResponse: message,
                responseDate: new Date(),
                respondingKid: kidId
              }
            : invite
        )
      );
    } catch (err) {
      console.error('Error responding to invite:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateInviteStatus = async (inviteId, status) => {
    try {
      const currentInvite = familyTimeInvites.find(inv => inv.id === inviteId);
      
      // If changing FROM accepted TO something else, remove from calendar
      if (currentInvite?.status === 'accepted' && status !== 'accepted') {
        const relatedCalendarEvents = familyCalendar.filter(event => 
          event.family_time_invite === inviteId || event.familyTimeInviteId === inviteId
        );
        
        for (const event of relatedCalendarEvents) {
          try {
            await removeCalendarEvent(event.calendarId || event.id);
          } catch (calendarError) {
            console.warn('Could not remove calendar event:', calendarError);
          }
        }
      }
      
      await supabaseService.updateFamilyTimeInvite(inviteId, { status });
      setFamilyTimeInvites(prev =>
        prev.map(invite =>
          invite.id === inviteId ? { ...invite, status } : invite
        )
      );
    } catch (err) {
      console.error('Error updating invite status:', err);
      setError(err.message);
      throw err;
    }
  };

  const editFamilyTimeInvite = async (inviteId, updatedInvite) => {
    try {
      await supabaseService.updateFamilyTimeInvite(inviteId, updatedInvite);
      setFamilyTimeInvites(prev =>
        prev.map(invite =>
          invite.id === inviteId ? { ...invite, ...updatedInvite } : invite
        )
      );
    } catch (err) {
      console.error('Error editing 1:1 invite:', err);
      setError(err.message);
      throw err;
    }
  };

  const deleteFamilyTimeInvite = async (inviteId) => {
    try {
      // Remove any associated calendar events first
      const relatedCalendarEvents = familyCalendar.filter(event => 
        event.family_time_invite_id === inviteId || event.familyTimeInviteId === inviteId
      );
      
      for (const event of relatedCalendarEvents) {
        try {
          await removeCalendarEvent(event.calendarId || event.id);
        } catch (calendarError) {
          console.warn('Could not remove calendar event:', calendarError);
        }
      }
      
      await supabaseService.deleteFamilyTimeInvite(inviteId);
      setFamilyTimeInvites(prev =>
        prev.filter(invite => invite.id !== inviteId)
      );
    } catch (err) {
      console.error('Error deleting 1:1 invite:', err);
      setError(err.message);
      throw err;
    }
  };

  // Allowance functions
  const updateKidAllowanceSettings = async (kidId, updates) => {
    try {
      const updatedAllowance = await supabaseService.updateKidAllowance(kidId, updates);
      setKidAllowances(prev => ({
        ...prev,
        [kidId]: updatedAllowance
      }));
      return updatedAllowance;
    } catch (err) {
      console.error('Error updating allowance settings:', err);
      setError(err.message);
      throw err;
    }
  };

  const addAllowanceTransaction = async (kidId, amount, type, reason) => {
    try {
      // Pass current user's ID as approvedBy for RLS policy compliance
      const approvedBy = currentUser?.id;
      if (!approvedBy) {
        throw new Error('No authorized user found to approve transaction');
      }
      
      const transaction = await supabaseService.addAllowanceTransaction(kidId, amount, type, reason, approvedBy);
      
      // Update transactions list
      setAllowanceTransactions(prev => ({
        ...prev,
        [kidId]: [transaction, ...(prev[kidId] || [])]
      }));
      
      // Update current balance
      const updatedAllowance = await supabaseService.getKidAllowance(kidId);
      setKidAllowances(prev => ({
        ...prev,
        [kidId]: updatedAllowance
      }));
      
      return transaction;
    } catch (err) {
      console.error('Error adding allowance transaction:', err);
      setError(err.message);
      throw err;
    }
  };

  const recordKidSpending = async (kidId, amount, reason) => {
    try {
      const transaction = await supabaseService.recordSpendingRequest(kidId, amount, reason);
      
      // Update transactions list
      setAllowanceTransactions(prev => ({
        ...prev,
        [kidId]: [transaction, ...(prev[kidId] || [])]
      }));
      
      // Update current balance since this is an immediate deduction
      const updatedAllowance = await supabaseService.getKidAllowance(kidId);
      setKidAllowances(prev => ({
        ...prev,
        [kidId]: updatedAllowance
      }));
      
      return transaction;
    } catch (err) {
      console.error('Error recording spending request:', err);
      setError(err.message);
      throw err;
    }
  };

  const addWeeklyAllowance = async (kidId) => {
    try {
      const allowance = kidAllowances[kidId];
      if (!allowance?.enabled || allowance.weekly_amount <= 0) {
        throw new Error('Weekly allowance is not enabled or amount is zero');
      }
      
      const transaction = await supabaseService.addWeeklyAllowance(
        kidId, 
        allowance.weekly_amount,
        'Weekly allowance'
      );
      
      // Update transactions list
      setAllowanceTransactions(prev => ({
        ...prev,
        [kidId]: [transaction, ...(prev[kidId] || [])]
      }));
      
      // Update current balance and last weekly addition
      const updatedAllowance = await supabaseService.getKidAllowance(kidId);
      setKidAllowances(prev => ({
        ...prev,
        [kidId]: updatedAllowance
      }));
      
      return transaction;
    } catch (err) {
      console.error('Error adding weekly allowance:', err);
      setError(err.message);
      throw err;
    }
  };

  // Job functions
  const createJob = async (jobData) => {
    try {
      const job = await supabaseService.createJob(jobData, currentUser?.id);
      setAllowanceJobs(prev => [job, ...prev]);
      return job;
    } catch (err) {
      console.error('Error creating job:', err);
      setError(err.message);
      throw err;
    }
  };

  const acceptJob = async (jobId, kidId) => {
    try {
      const updatedJob = await supabaseService.acceptJob(jobId, kidId);
      setAllowanceJobs(prev => 
        prev.map(job => job.id === jobId ? updatedJob : job)
      );
      return updatedJob;
    } catch (err) {
      console.error('Error accepting job:', err);
      setError(err.message);
      throw err;
    }
  };

  const completeJob = async (jobId, kidId) => {
    try {
      const updatedJob = await supabaseService.completeJob(jobId, kidId);
      setAllowanceJobs(prev => 
        prev.map(job => job.id === jobId ? updatedJob : job)
      );
      return updatedJob;
    } catch (err) {
      console.error('Error completing job:', err);
      setError(err.message);
      throw err;
    }
  };

  const approveJobCompletion = async (jobId) => {
    try {
      const updatedJob = await supabaseService.approveJobCompletion(jobId, currentUser?.id);
      
      // Update job status
      setAllowanceJobs(prev => 
        prev.map(job => job.id === jobId ? updatedJob : job)
      );
      
      // Refresh allowance data for the kid who completed the job
      const updatedAllowance = await supabaseService.getKidAllowance(updatedJob.accepted_by);
      const transactions = await supabaseService.getAllowanceTransactions(updatedJob.accepted_by);
      
      setKidAllowances(prev => ({
        ...prev,
        [updatedJob.accepted_by]: updatedAllowance
      }));
      
      setAllowanceTransactions(prev => ({
        ...prev,
        [updatedJob.accepted_by]: transactions
      }));
      
      return updatedJob;
    } catch (err) {
      console.error('Error approving job completion:', err);
      setError(err.message);
      throw err;
    }
  };

  const rejectJobCompletion = async (jobId, reason) => {
    try {
      const updatedJob = await supabaseService.rejectJobCompletion(jobId, reason);
      setAllowanceJobs(prev => 
        prev.map(job => job.id === jobId ? updatedJob : job)
      );
      return updatedJob;
    } catch (err) {
      console.error('Error rejecting job completion:', err);
      setError(err.message);
      throw err;
    }
  };

  // User switching function with persistence
  const switchUser = (userToSwitchTo) => {
    devLog('🔄 Switching user to:', userToSwitchTo);
    
    // SAFEGUARD: If switching to a real user (not mock), clear any testing flags
    // BUT preserve testing-mode if we're in an active test session
    if (userToSwitchTo && !userToSwitchTo.isMock) {
      const isTestingSession = localStorage.getItem('testing-mode') === 'true';
      devLog('🔄 Switching to real user, clearing testing flags', { isTestingSession });
      
      if (!isTestingSession) {
        localStorage.removeItem('testing-mode');
      }
      localStorage.removeItem('testing-profile-id');
      localStorage.removeItem('mock-college-preferences');
    }
    
    // Preserve the original parent's authentication when switching to kid view
    let userWithOriginalParent = { ...userToSwitchTo };
    
    if (userToSwitchTo && !userToSwitchTo.isParent && user && family) {
      // When switching to a kid, preserve the original parent's auth info
      const originalParent = family.parents?.find(p => p.authUserId === user.id);
      if (originalParent) {
        userWithOriginalParent.originalParent = originalParent;
        userWithOriginalParent.originalAuthUserId = user.id;
        devLog('🔄 Preserving original parent for kid view:', originalParent.name);
      }
    }
    
    // When switching back to parent view, restore original parent properties
    if (userToSwitchTo && userToSwitchTo.isParent && currentUser && currentUser.originalParent) {
      userWithOriginalParent.originalParent = currentUser.originalParent;
      userWithOriginalParent.originalAuthUserId = currentUser.originalAuthUserId;
      devLog('🔄 Restoring original parent properties');
    }
    
    setCurrentUser(userWithOriginalParent);
    
    // Persist the selected user across refreshes and navigation
    try {
      if (userToSwitchTo) {
        localStorage.setItem('selected-user-single-family', JSON.stringify({
          userId: userToSwitchTo.id,
          isParent: userToSwitchTo.isParent,
          type: userToSwitchTo.type,
          name: userToSwitchTo.name,
          originalParent: userWithOriginalParent.originalParent,
          originalAuthUserId: userWithOriginalParent.originalAuthUserId,
          timestamp: Date.now()
        }));
        devLog('✅ Persisted user selection to localStorage');
      }
    } catch (error) {
      devWarn('Failed to persist user selection:', error);
    }
    
    // No need to invalidate cache on user switch - data should be cached per user
    // Cache will be automatically used for the new user if available
    devLog('✅ User switched without cache invalidation');
  };

  // Auth functions
  const signUp = async (email, password, metadata = {}) => {
    try {
      const data = await authService.signUp(email, password, metadata);
      return data;
    } catch (err) {
      console.error('Error signing up:', err);
      setError(err.message);
      throw err;
    }
  };

  const signIn = async (email, password) => {
    try {
      const data = await authService.signIn(email, password);
      return data;
    } catch (err) {
      console.error('Error signing in:', err);
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
    } catch (err) {
      console.error('Error signing out:', err);
      setError(err.message);
      throw err;
    }
  };

  // Public method to refresh family data
  const refreshFamilyData = async () => {
    if (user?.id) {
      setIsRefreshing(true);
      try {
        await loadFamilyData(user.id);
        
        // Run auto-archiving for past activities (but only for parents to avoid conflicts)
        if (currentUser?.isParent || currentUser?.originalParent) {
          try {
            const archiveResult = await autoArchivePastActivities();
            if (archiveResult.archived > 0) {
              console.log(`✅ Auto-archived ${archiveResult.archived} past activities`);
            }
          } catch (archiveError) {
            console.warn('⚠️ Auto-archive failed (non-critical):', archiveError);
            // Don't throw - auto-archiving failure shouldn't break the refresh
          }
        }
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const value = {
    user,
    family,
    currentUser,
    familyUsers,
    activities,
    plans,
    patterns,
    familyActivities,
    familyCalendar,
    recsIdeas,
    familyTimeInvites,
    kidAllowances,
    allowanceTransactions,
    allowanceJobs,
    personalModules,
    setPersonalModules,
    loading,
    authInitialized,
    error,
    createFamily,
    setCurrentUser,
    switchUser,
    addActivity,
    editActivity,
    deleteActivity,
    markActivityViewed,
    updatePlan,
    updateActivityStatus,
    addFamilyActivity,
    editFamilyActivity,
    updateFamilyActivity,
    updateFamilyActivityStatus,
    cancelFamilyActivity,
    archiveFamilyActivity,
    unarchiveFamilyActivity,
    markActivityCompleted,
    toggleActivityFavorite,
    autoArchivePastActivities,
    voteOnFamilyActivity,
    restoreActivityImages,
    rateActivity,
    scheduleToCalendar,
    addPotentialDatesToFamilyActivity,
    addActivityToCalendar,
    editCalendarEvent,
    removeCalendarEvent,
    // Ideas & Opportunities
    addIdea,
    updateIdeaStatus,
    editIdea,
    deleteIdea,
    // 1:1 Invites
    addFamilyTimeInvite,
    respondToInvite,
    updateInviteStatus,
    editFamilyTimeInvite,
    deleteFamilyTimeInvite,
    // Allowance
    updateKidAllowanceSettings,
    addAllowanceTransaction,
    recordKidSpending,
    addWeeklyAllowance,
    // Jobs
    createJob,
    acceptJob,
    completeJob,
    approveJobCompletion,
    rejectJobCompletion,
    // Auth
    signUp,
    signIn,
    signOut,
    refreshFamilyData
  };

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  );
}

// Export everything as named exports
export { FamilyContext, useFamilyContext, FamilyProvider };